/**
 * Where an instance stands in each stage (Valmistelu, Toteutus, Tulokset):
 * checklist items, progress and the one next step, from data other endpoints
 * already return — the instance row, the chain view and the admin overview
 * counts (specs/admin-instance-lifecycle.md, #744). Pure: no fetching, no
 * wording; Yleiskuva turns keys into text with t('stageStatus.<key>', values).
 *
 * An item is { key, done, optional?, values?, path, hash?, script? }. Optional
 * items show but do not count towards the stage being complete. `script`
 * names a step that has no UI yet: { command, skill }.
 */
import API_ENDPOINTS from '../config/api';

// kuutio_instances.status → the stage the instance is in.
const STAGE_BY_STATUS = {
  draft: 'prepare',
  'pre-workshop': 'prepare',
  running: 'run',
  'post-workshop': 'results',
  archived: 'results',
};

export const STAGE_ORDER = ['prepare', 'run', 'results'];

/** The status a stage move sets: into Toteutus and into Tulokset. */
export const STATUS_FOR_STAGE = { run: 'running', results: 'post-workshop' };

export function currentStage(status) {
  return STAGE_BY_STATUS[status] || 'prepare';
}

function summarise(items) {
  const required = items.filter((i) => !i.optional);
  const done = required.filter((i) => i.done).length;
  return {
    items,
    done,
    total: required.length,
    complete: done === required.length,
    next: required.find((i) => !i.done) || null,
  };
}

function prepareItems(instance, chain, overview) {
  const settings = instance.settings || {};
  const moduleSteps = (chain?.steps || []).filter((s) => s.moduleId !== 'report.v1');
  const manualNeeded = chain?.issues?.manualNeeded || [];
  return [
    { key: 'goal', done: Boolean(settings.workshopGoal?.trim()), path: '/admin/settings', hash: 'tavoite' },
    {
      key: 'language', done: Boolean(instance.language), path: '/admin/settings', hash: 'kieli',
      values: { language: (instance.language || 'fi').toUpperCase() },
    },
    { key: 'flow', done: moduleSteps.length > 0, path: '/admin/flow', values: { modules: moduleSteps.length } },
    {
      // Inputs known before the Kuutio are entered on Sisältö (#747); link to the first missing one
      key: 'inputs', done: moduleSteps.length > 0 && manualNeeded.length === 0, path: '/admin/content',
      hash: manualNeeded[0] ? `tieto-${manualNeeded[0]}` : undefined,
      values: { missing: manualNeeded.length },
    },
    {
      key: 'scenarios', done: overview.scenarios > 0, path: '/admin/content', hash: 'skenaariot',
      values: { count: overview.scenarios },
    },
    {
      key: 'access', done: Boolean(settings.pin || settings.hasPin) || overview.pins > 0, path: '/admin/participants', hash: 'pin',
      values: { count: overview.pins },
    },
    {
      key: 'datasets', done: overview.datasets > 0, path: '/admin/datasets',
      values: { count: overview.datasets, pending: overview.intake_pending },
    },
    {
      key: 'slides', optional: true, done: Boolean(instance.google_presentation_id), path: '/admin/screen-content',
    },
    {
      key: 'organisations', optional: true, done: overview.organisations > 0, path: '/admin/participants',
      values: { count: overview.organisations },
      script: { command: 'DATABASE_URL="$DB_PROD" ONLY=kuutio_N node scripts/import-participants.js', skill: 'participant-import' },
    },
  ];
}

function runItems(instance, chain) {
  const steps = chain?.steps || [];
  const liveIndex = steps.findIndex((s) => s.isLive);
  const last = steps[steps.length - 1];
  return [
    { key: 'started', done: Boolean(chain?.started), path: '/admin/workshop' },
    {
      key: 'progress', done: Boolean(last?.reachedAt), path: '/admin/workshop',
      values: { current: liveIndex >= 0 ? liveIndex + 1 : 0, total: steps.length },
    },
    { key: 'devices', optional: true, done: false, path: '/screen-manager' },
  ];
}

function resultsItems(instance, overview, instanceId) {
  return [
    { key: 'report', done: overview.reports > 0, path: '/reportgen' },
    {
      key: 'translations', optional: true, done: overview.translations.length > 0, path: '/reportgen',
      values: { languages: overview.translations.map((l) => l.toUpperCase()).join(', ') },
    },
    {
      key: 'transcripts', optional: true, done: overview.transcripts > 0, path: '/reportgen',
      values: { count: overview.transcripts },
      script: { command: `DATABASE_URL="$DB_PROD" node apps/backend/src/scripts/import-transcript.js ${instanceId} <file>_final.txt "<phase>"`, skill: 'transcribe' },
    },
    {
      key: 'whatif', optional: true, done: overview.whatif_texts > 0, path: '/reportgen',
      values: { count: overview.whatif_texts },
      script: { command: `DATABASE_URL="$DB_PROD" node apps/backend/src/scripts/populate-whatif-texts.js ${instanceId} --dry-run`, skill: 'report-generation' },
    },
    // The summary is the report step's action "Kirjoita tiivistelmä" in Työpaja (#1230)
    { key: 'summary', optional: true, done: Boolean(overview.has_summary), path: '/admin/workshop' },
    { key: 'export', optional: true, done: false, path: '/admin/export' },
  ];
}

/**
 * @param {{ instance: object, chain?: object|null, overview: object }} data
 * @returns {{ current: string, stages: Record<string, ReturnType<typeof summarise>> }}
 */
export function buildStageStatus({ instance, chain = null, overview }) {
  return {
    current: currentStage(instance.status),
    stages: {
      prepare: summarise(prepareItems(instance, chain, overview)),
      run: summarise(runItems(instance, chain)),
      results: summarise(resultsItems(instance, overview, instance.id)),
    },
  };
}

/** Link target of an item, keeping the instance. */
export function itemHref(item, instanceId) {
  return `${item.path}?instance=${instanceId}${item.hash ? `#${item.hash}` : ''}`;
}
